import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../../components/common/Icon/Icon';
import Button from '../../../components/common/Button/Button';
import Loading from '../../../components/common/Loading/Loading';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import PhotoUploadButton from '../../../components/common/PhotoUploadButton/PhotoUploadButton';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Badge from '../../../components/common/Badge/Badge';
import ProjectBadge from '../../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../../components/common/ReactivatedBadge/ReactivatedBadge';
import AvatarProfile from '../../../components/common/AvatarProfile/AvatarProfile';
import useProfilePhotoUpload from '../../../hooks/useProfilePhotoUpload';

import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DeleteStudentConfirmModal from '../../../components/students/DeleteStudentConfirmModal/DeleteStudentConfirmModal';
import StudentInactivationReasonModal from '../../../components/students/StudentInactivationReasonModal/StudentInactivationReasonModal';
import { getStudentInactivationReasonLabel } from '../../../constants/studentInactivationReason';
import { AdminTabbedPanel } from '../../../components/common/AdminPageLayout/AdminPageLayout';
import { useBreadcrumbs, BreadcrumbItem } from '../../../contexts/BreadcrumbContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  StudentService,
  AddressService,
  AnamnesisService,
  PhoneService,
  PhotoService,
  CoachService,
} from '../../../services';
import {
  mapAnamnesisToStudentRegisterForms,
  mapStudentRegisterAnamnesisToRecord,
  mapStudentRegisterParqToAnamnesisRecord,
} from '../../../utils/studentRegisterSubmission';
import { formatDate } from '../../../utils';
import type { CoachOption } from '../../../services/CoachService';
import PaymentDetails from '../../../components/students/PaymentDetails/PaymentDetails';
import { maskCpf, maskPhoneByCountry, maskPostalCode, maskInstagram, formatHeightMeters, formatWeightKg, parseHeightMeters, parseWeightKg } from '../../../utils/masks';
import { unmaskCpf, unmaskPhone, unmaskHeight, unmaskWeight, unmaskInstagram } from '../../../utils/masks';
import { extractCallingCode, resolveCallingCode, CODE_TO_ISO } from '../../../constants/callingCodes';
import config from '../../../config/environment';
import { validateSignUpProfile, validateAnamnese, validateParQ } from '../../../utils/validation';
import type {
  SignUpProfileFormData,
  AnamneseFormData,
  ParQFormData,
  PhotosFormData,
  PhotoFile,
  OnboardingState,
} from '../../../types/registration';
import {
  PHOTO_FORM_KEY_TO_POSITION,
  createEmptyPhotosFormData,
  serializePhotosSnapshot,
  type PhotoPosition,
} from '../../../types/photo';
import {
  canViewStudentBehavioralProfile,
  canViewStudentPayments,
  canChangeStudentCoach,
  canEditExistingStudent as userCanEditExistingStudent,
  canEditStudentPersonalInfo,
  canInactivateStudents,
  canManageStudents,
} from '../../../utils/permissions';
import SignUpProfileStep from './steps/SignUpProfileStep';
import AnamneseStep from './steps/AnamneseStep';
import ParQStep from './steps/ParQStep';
import PhotoGuidelinesStep from './steps/PhotoGuidelinesStep';
import StudentPublicLinkCopyButton, { type StudentPublicLinkTarget } from './StudentPublicLinkCopyButton';
import StudentHistoryStep from './steps/StudentHistoryStep';
import StudentTrainingsStep from './steps/StudentTrainingsStep';
import BehavioralProfileStep from './steps/BehavioralProfileStep';
import PerformanceSupportSection from './steps/PerformanceSupportSection';
import StudentEquipmentsStep from './steps/StudentEquipmentsStep';
import type { StudentBodyPhotoAssessment } from '../../../services/PhotoService';
import {
  bodyPhotoAssessmentDatePartsToIso,
  getTodayBodyPhotoAssessmentDateParts,
  isValidBodyPhotoAssessmentDateParts,
  type BodyPhotoAssessmentDateParts,
} from '../../../utils/studentBodyPhotoAssessmentDateFields';
import ProfileColumns from '../../../components/common/ProfileColumns/ProfileColumns';
import StickyHeader from '../../../components/common/StickyHeader/StickyHeader';
import type { Anamnesis, ProjectIdentity, StudentClassification } from '../../../types';
import {
  shouldDisplayStudentClassificationBadge,
  getStudentClassificationBadgeVariant,
} from '../../../types';
import styles from './StudentRegister.module.css';

const STEPS = [
  { key: 'personal-info', label: 'Informações básicas' },
  { key: 'anamnesis', label: 'Anamnese' },
  { key: 'parq', label: 'Par-Q' },
  { key: 'photos', label: 'Fotos' },
  { key: 'equipments', label: 'Equipamentos' },
  { key: 'payments', label: 'Planos e pagamentos' },
] as const;

const HISTORY_STEP = { key: 'history', label: 'Histórico' } as const;
const TRAININGS_STEP = { key: 'trainings', label: 'Treinos' } as const;
const BEHAVIORAL_PROFILE_STEP = { key: 'behavioral-profile', label: 'Perfil comportamental' } as const;
const STUDENT_SECTION_KEYS = [
  'personal-info',
  'anamnesis',
  'parq',
  'photos',
  'equipments',
  'payments',
  'trainings',
  'behavioral-profile',
  'history',
] as const;
type StudentSectionKey = typeof STUDENT_SECTION_KEYS[number];
const STUDENT_REGISTER_QUERY_SCHEMA = {
  section: queryFilter.enum<StudentSectionKey>('personal-info', STUDENT_SECTION_KEYS),
};

const initialSignUpProfile = (): SignUpProfileFormData => ({
  name: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  cpf: '',
  birthDate: '',
  callingCode: '55',
  phone: '',
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  state: '',
  city: '',
  country: 'BR',
  joinedAt: '',
  instagram: '',
  height: '',
  weight: '',
  coachId: '',
});

const initialAnamnese = (): AnamneseFormData => ({
  heightCm: '',
  weightKg: '',
  profession: '',
  instagram: '',
  onlineCourse: '',
  otherCourse: '',
  previousOnlineConsultancy: '',
  courseFeedback: '',
  trainingLevel: '',
  trainingLevelOther: '',
  trainingLocation: '',
  availableEquipment: '',
  fullGymName: '',
  weeklyAvailability: '',
  availableTrainingTime: '',
  hadInjury: null,
  injuryDescription: '',
  hasPain: null,
  painLocation: '',
  painLimitsTraining: null,
  limitedExercises: '',
  currentlyExercises: null,
  exerciseModalities: '',
  timeWithoutExercise: '',
  exerciseHistory: '',
  enjoysTraining: null,
  usedToEffort: null,
  hasNutritionalSupport: null,
  dietDetail: '',
  takesMedication: null,
  medications: '',
  hadSurgery: null,
  surgeries: '',
  hasChronicDisease: null,
  chronicDiseases: '',
  hasFamilyHistory: null,
  familyHistoryDescription: '',
  hasChildren: null,
  timeSinceDelivery: '',
  abdominalDiastasis: null,
  diastasisTreatment: '',
  mainGoal: '',
  goalDetails: '',
  additionalInfo: '',
  referralSource: '',
  motivation: '',
});

const initialParQ = (): ParQFormData => ({
  heartCondition: null,
  heartConditionOther: '',
  chestPainActivity: null,
  chestPainRecent: null,
  dizziness: null,
  boneJointProblem: null,
  takesBpMedication: null,
  otherReason: null,
  responsibility: '',
});

const initialPhotos = createEmptyPhotosFormData;

const orNull = (v: string) => (v.trim() ? v.trim() : null);

interface ProfileSaveResult {
  studentId: number;
  shouldRedirect: boolean;
}

const StudentRegister: React.FC = () => {
  const { id: studentIdParam } = useParams<{ id: string }>();
  const editMode = Boolean(studentIdParam);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { loading: authLoading, roles } = useAuth();
  const canManageStudent = canManageStudents(roles);
  const canInactivateStudent = canInactivateStudents(roles);
  const canEditExistingStudent = userCanEditExistingStudent(roles);
  const canEditPersonalInfo = canEditStudentPersonalInfo(roles);
  const canSaveStudent = editMode ? canEditExistingStudent : canManageStudent;
  const canSavePersonalInfo = editMode ? canEditPersonalInfo : canManageStudent;
  const isSupportPersonalInfoOnly = editMode && canSavePersonalInfo && !canSaveStudent;
  const canAssignCoach = canChangeStudentCoach(roles);
  const canViewPayments = canViewStudentPayments(roles);
  const canViewBehavioralProfile = canViewStudentBehavioralProfile(roles);
  const steps = useMemo(() => {
    const baseSteps = canViewPayments
      ? [...STEPS]
      : STEPS.filter((step) => step.key !== 'payments');

    if (!editMode) {
      return baseSteps;
    }

    return [
      ...baseSteps,
      TRAININGS_STEP,
      ...(canViewBehavioralProfile ? [BEHAVIORAL_PROFILE_STEP] : []),
      HISTORY_STEP,
    ];
  }, [canViewBehavioralProfile, canViewPayments, editMode]);
  const {
    filters: { section },
    setFilter: setStudentRegisterQueryFilter,
  } = useQueryFilters(STUDENT_REGISTER_QUERY_SCHEMA);
  const activeKey = steps.some((step) => step.key === section)
    ? section
    : steps[0]?.key ?? '';
  const setActiveKey = useCallback((key: string) => {
    if ((STUDENT_SECTION_KEYS as readonly string[]).includes(key)) {
      setStudentRegisterQueryFilter('section', key as StudentSectionKey, { resetPage: false });
    }
  }, [setStudentRegisterQueryFilter]);
  const [signUpProfile, setSignUpProfile] = useState<SignUpProfileFormData>(initialSignUpProfile);
  const [anamnese, setAnamnese] = useState<AnamneseFormData>(initialAnamnese);
  const [anamneseAiSummary, setAnamneseAiSummary] = useState<string | null>(null);
  const [anamnesisRecords, setAnamnesisRecords] = useState<Anamnesis[]>([]);
  const [selectedAnamnesisId, setSelectedAnamnesisId] = useState<number | null>(null);
  const anamnesisProfileDefaultsRef = useRef<Pick<AnamneseFormData, 'heightCm' | 'weightKg' | 'instagram'>>({
    heightCm: '',
    weightKg: '',
    instagram: '',
  });
  const [parq, setParQ] = useState<ParQFormData>(initialParQ);
  const [photos, setPhotos] = useState<PhotosFormData>(initialPhotos);
  const [bodyPhotoAssessmentDateParts, setBodyPhotoAssessmentDateParts] = useState<BodyPhotoAssessmentDateParts>(
    () => getTodayBodyPhotoAssessmentDateParts(),
  );
  const [pendingPhotoDeletions, setPendingPhotoDeletions] = useState<PhotoPosition[]>([]);
  const [photoAssessments, setPhotoAssessments] = useState<StudentBodyPhotoAssessment[]>([]);
  const [primaryBodyPhotoAssessmentDate, setPrimaryBodyPhotoAssessmentDate] = useState<string | null>(null);
  const [equipmentRefreshKey, setEquipmentRefreshKey] = useState(0);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const photosLoadedStudentIdRef = useRef<number | null>(null);
  const [isInactive, setIsInactive] = useState<boolean>(false);
  const [isForeignStudent, setIsForeignStudent] = useState<boolean>(false);
  const [isTradeAgreement, setIsTradeAgreement] = useState<boolean>(false);
  const [classification, setClassification] = useState<StudentClassification | null>(null);
  const [project, setProject] = useState<ProjectIdentity | null>(null);
  const [isReactivated, setIsReactivated] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingState>({ userId: null, studentId: null });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(editMode);
  const [loadingCep, setLoadingCep] = useState(false);
  const [coachOptions, setCoachOptions] = useState<CoachOption[]>([]);
  const [loadingCoachOptions, setLoadingCoachOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInactivateModal, setShowInactivateModal] = useState(false);
  const [showInactivateReasonModal, setShowInactivateReasonModal] = useState(false);
  const [inactivationReason, setInactivationReason] = useState('');
  const [inactivationObservation, setInactivationObservation] = useState<string | null>(null);
  const [latestInactivationReason, setLatestInactivationReason] = useState<string | null>(null);
  const [latestInactivationObservation, setLatestInactivationObservation] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const profilePhotoInputRef = useRef<HTMLInputElement | null>(null);

  /** Original inactive flag loaded from DB — used to lock the form */
  const [savedIsInactive, setSavedIsInactive] = useState(false);

  /* Snapshot refs for dirty detection */
  const snapshotProfile = useRef<string>(JSON.stringify(initialSignUpProfile()));
  const snapshotAnamnese = useRef<string>(JSON.stringify(initialAnamnese()));
  const snapshotParq = useRef<string>(JSON.stringify(initialParQ()));
  const snapshotIsInactive = useRef<boolean>(false);
  const snapshotIsForeignStudent = useRef<boolean>(false);
  const snapshotIsTradeAgreement = useRef<boolean>(false);
  const snapshotCoachId = useRef<string>('');
  const snapshotPhotos = useRef<string>(serializePhotosSnapshot(createEmptyPhotosFormData(), []));

  const applyAnamnesisRecordToForms = useCallback((record: Anamnesis) => {
    const mapped = mapAnamnesisToStudentRegisterForms(record, anamnesisProfileDefaultsRef.current);
    setAnamnese(mapped.anamnese);
    snapshotAnamnese.current = JSON.stringify(mapped.anamnese);
    setParQ(mapped.parq);
    snapshotParq.current = JSON.stringify(mapped.parq);
    setAnamneseAiSummary(mapped.aiSummary);
    setSelectedAnamnesisId(record.id);
    setErrors({});
  }, []);

  const handleAnamnesisRecordChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextId = Number(event.target.value);
    const record = anamnesisRecords.find((item) => item.id === nextId);
    if (!record) {
      return;
    }

    applyAnamnesisRecordToForms(record);
  }, [anamnesisRecords, applyAnamnesisRecordToForms]);

  const latestAnamnesisId = anamnesisRecords[0]?.id ?? null;
  const isViewingLatestAnamnesis = selectedAnamnesisId === null
    || selectedAnamnesisId === latestAnamnesisId;

  const renderAnamnesisRecordSelector = (label: string) => {
    if (!editMode || anamnesisRecords.length === 0) {
      return null;
    }

    return (
      <div className={styles.anamnesisRecordSelector}>
        <FormSelect
          label={label}
          id="anamnesis-record"
          value={String(selectedAnamnesisId ?? anamnesisRecords[0]?.id ?? '')}
          onChange={handleAnamnesisRecordChange}
          options={anamnesisRecords.map((record) => ({
            value: String(record.id),
            label: formatDate(record.created_at),
          }))}
          disabled={isReadOnly}
        />
      </div>
    );
  };

  const {
    displayUrl: profilePhotoUrl,
    hasPendingUpload: hasPendingProfilePhoto,
    isUploading: isUploadingProfilePhoto,
    feedback: profilePhotoFeedback,
    selectFile: selectProfilePhoto,
    uploadPendingPhoto,
    clearFeedback: clearProfilePhotoFeedback,
  } = useProfilePhotoUpload({
    studentId: onboarding.studentId,
    userId: onboarding.userId,
  });

  const isCoachOnlyEditor = editMode && canAssignCoach && !canSavePersonalInfo;
  const isCoachAssignmentDirty = signUpProfile.coachId !== snapshotCoachId.current;

  const isDirty = useMemo(() => {
    if (!editMode) return true; // new student — always allow save
    const personalInfoDirty =
      JSON.stringify(signUpProfile) !== snapshotProfile.current ||
      isForeignStudent !== snapshotIsForeignStudent.current ||
      isTradeAgreement !== snapshotIsTradeAgreement.current ||
      isCoachAssignmentDirty;

    if (isCoachOnlyEditor) {
      return isCoachAssignmentDirty;
    }

    if (isSupportPersonalInfoOnly) {
      return personalInfoDirty || isInactive !== snapshotIsInactive.current;
    }

    return (
      personalInfoDirty ||
      JSON.stringify(anamnese) !== snapshotAnamnese.current ||
      JSON.stringify(parq) !== snapshotParq.current ||
      isInactive !== snapshotIsInactive.current ||
      hasPendingProfilePhoto ||
      serializePhotosSnapshot(photos, pendingPhotoDeletions) !== snapshotPhotos.current
    );
  }, [editMode, isCoachOnlyEditor, isCoachAssignmentDirty, isSupportPersonalInfoOnly, signUpProfile, anamnese, parq, isInactive, isForeignStudent, isTradeAgreement, hasPendingProfilePhoto, photos, pendingPhotoDeletions]);

  const hasPendingBodyPhotoChanges = useMemo(() => {
    const hasUploads = (Object.keys(photos) as Array<keyof PhotosFormData>)
      .some((key) => Boolean(photos[key]?.file));
    return hasUploads || pendingPhotoDeletions.length > 0;
  }, [photos, pendingPhotoDeletions]);

  const canReactivateInactiveStudent = savedIsInactive && canManageStudent;
  const canChangeStudentStatus = canManageStudent || (canInactivateStudent && !savedIsInactive);
  const isReactivating = canReactivateInactiveStudent && !isInactive;
  const isReadOnly = savedIsInactive || !canSaveStudent;
  const isPersonalInfoReadOnly = savedIsInactive || !canSavePersonalInfo;
  const canChangeCoach = canAssignCoach && !savedIsInactive;
  const isCoachFieldReadOnly = isPersonalInfoReadOnly && !canChangeCoach;
  const isPersonalInfoSectionLocked = isPersonalInfoReadOnly && !canChangeCoach;
  useEffect(() => {
    if (profilePhotoFeedback?.variant !== 'success') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      clearProfilePhotoFeedback();
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [clearProfilePhotoFeedback, profilePhotoFeedback]);

  useEffect(() => {
    let isCancelled = false;

    const loadCoachOptions = async () => {
      try {
        setLoadingCoachOptions(true);
        const options = await CoachService.listOptions();

        if (!isCancelled) {
          setCoachOptions(options);
        }
      } catch {
        if (!isCancelled) {
          setCoachOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingCoachOptions(false);
        }
      }
    };

    void loadCoachOptions();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editMode || !studentIdParam) return;
    let cancelled = false;

    setLoadingData(true);
    setError(null);
    setOnboarding({ userId: null, studentId: null });
    setClassification(null);
    setProject(null);
    // Reset photo state when switching students so the Photos tab reloads on demand.
    photosLoadedStudentIdRef.current = null;
    setPhotoAssessments([]);
    setPhotos(createEmptyPhotosFormData());
    setPendingPhotoDeletions([]);
    setAnamneseAiSummary(null);
    setAnamnesisRecords([]);
    setSelectedAnamnesisId(null);
    setEquipmentRefreshKey((key) => key + 1);
    snapshotPhotos.current = serializePhotosSnapshot(createEmptyPhotosFormData(), []);

    async function loadStudentData() {
      try {
        const [student, phone, address, anamnesisRows, coachAssociation, inactivationReasonFromHistory] = await Promise.all([
          StudentService.findById(studentIdParam!),
          PhoneService.findByStudentId(Number(studentIdParam)),
          AddressService.findByStudentId(Number(studentIdParam)),
          AnamnesisService.findAllByStudentId(Number(studentIdParam)),
          StudentService.getCoachAssociation(Number(studentIdParam)),
          StudentService.getLatestInactivationReason(Number(studentIdParam)),
        ]);
        if (cancelled) return;

        setAnamnesisRecords(anamnesisRows);
        if (!student) throw new Error('Aluna não encontrada');

        setOnboarding({ userId: student.user_id || null, studentId: student.id });
        setClassification(student.classification ?? null);
        setProject(student.project ?? null);
        setIsReactivated(student.is_reactivated === true);

        const studentIsInactive = student.is_inactive ?? false;
        const studentIsForeign = student.is_foreign_student ?? false;
        const studentIsTradeAgreement = student.is_trade_agreement ?? false;
        setIsInactive(studentIsInactive);
        setIsForeignStudent(studentIsForeign);
        setIsTradeAgreement(studentIsTradeAgreement);
        setSavedIsInactive(studentIsInactive);
        snapshotIsInactive.current = studentIsInactive;
        snapshotIsForeignStudent.current = studentIsForeign;
        snapshotIsTradeAgreement.current = studentIsTradeAgreement;
        setLatestInactivationReason(studentIsInactive ? inactivationReasonFromHistory.reason : null);
        setLatestInactivationObservation(studentIsInactive ? inactivationReasonFromHistory.observation : null);

        const resolvedCode = resolveCallingCode(phone?.country_code || '55', phone?.country_iso ?? undefined);
        const profileData: SignUpProfileFormData = {
          name: (student.full_name || '').toUpperCase(),
          username: student.full_name || '',
          email: student.email || '',
          password: '',
          confirmPassword: '',
          cpf: student.cpf ? maskCpf(student.cpf) : '',
          birthDate: student.birth_date || '',
          callingCode: resolvedCode,
          phone: phone?.phone_number ? maskPhoneByCountry(phone.phone_number, resolvedCode) : '',
          country: address?.country ?? phone?.country_iso ?? 'BR',
          cep: address?.zipcode ? maskPostalCode(address.zipcode, address?.country ?? phone?.country_iso ?? 'BR') : '',
          street: address?.street || '',
          number: address?.street_number || '',
          complement: address?.complement || '',
          neighborhood: address?.neighborhood || '',
          state: address?.state || '',
          city: address?.city || '',
          joinedAt: student.joined_at || '',
          instagram: student.instagram_username ? maskInstagram(student.instagram_username) : '',
          height: student.height_m ? formatHeightMeters(student.height_m) : '',
          weight: student.weight_kg ? formatWeightKg(student.weight_kg) : '',
          coachId: coachAssociation.coach_id ? String(coachAssociation.coach_id) : '',
        };
        setSignUpProfile(profileData);
        snapshotProfile.current = JSON.stringify(profileData);
        snapshotCoachId.current = profileData.coachId;

        const heightCm = student.height_m
          ? formatHeightMeters(student.height_m)
          : '';
        const weightKg = student.weight_kg != null
          ? formatWeightKg(student.weight_kg)
          : '';
        const instagram = student.instagram_username
          ? maskInstagram(student.instagram_username)
          : '';

        anamnesisProfileDefaultsRef.current = { heightCm, weightKg, instagram };

        const latestAnamnesis = anamnesisRows[0] ?? null;
        if (latestAnamnesis) {
          applyAnamnesisRecordToForms(latestAnamnesis);
        } else {
          const emptyAnamnese = { ...initialAnamnese(), heightCm, weightKg, instagram };
          const emptyParq = initialParQ();
          setAnamnese(emptyAnamnese);
          snapshotAnamnese.current = JSON.stringify(emptyAnamnese);
          setParQ(emptyParq);
          snapshotParq.current = JSON.stringify(emptyParq);
          setAnamneseAiSummary(null);
          setSelectedAnamnesisId(null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Erro ao carregar dados da aluna.');
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }

    loadStudentData();
    return () => { cancelled = true; };
  }, [applyAnamnesisRecordToForms, editMode, studentIdParam]);

  /* Fotos: carregam sob demanda na aba Fotos, sem bloquear o loading da tela. */
  useEffect(() => {
    if (!editMode || !onboarding.studentId || activeKey !== 'photos' || loadingData) {
      return;
    }

    if (photosLoadedStudentIdRef.current === onboarding.studentId) {
      return;
    }

    let cancelled = false;

    async function loadPhotos() {
      setLoadingPhotos(true);

      try {
        const assessments = await PhotoService.listBodyPhotoAssessments(
          onboarding.studentId!,
          onboarding.userId,
        );

        if (!cancelled) {
          setPhotoAssessments(assessments);
          setPrimaryBodyPhotoAssessmentDate(assessments[0]?.assessmentDate ?? null);
          const emptyPhotos = createEmptyPhotosFormData();
          setPhotos(emptyPhotos);
          setPendingPhotoDeletions([]);
          snapshotPhotos.current = serializePhotosSnapshot(emptyPhotos, []);
          photosLoadedStudentIdRef.current = onboarding.studentId;
        }
      } catch {
        if (!cancelled) {
          setPhotoAssessments([]);
          setPhotos(createEmptyPhotosFormData());
          setPendingPhotoDeletions([]);
          snapshotPhotos.current = serializePhotosSnapshot(createEmptyPhotosFormData(), []);
        }
      } finally {
        if (!cancelled) {
          setLoadingPhotos(false);
        }
      }
    }

    void loadPhotos();
    return () => { cancelled = true; };
  }, [activeKey, editMode, loadingData, onboarding.studentId, onboarding.userId]);

  /* ── Breadcrumbs ── */
  useEffect(() => {
    const crumbs: BreadcrumbItem[] = [{ label: 'Alunas', path: '/alunas' }];
    if (editMode && signUpProfile.name) {
      crumbs.push({ label: signUpProfile.name });
    } else if (!editMode) {
      crumbs.push({ label: 'Nova aluna' });
    }
    setBreadcrumbs(crumbs);
    return () => setBreadcrumbs([]);
  }, [editMode, signUpProfile.name, setBreadcrumbs]);

  const updateSignUpProfile = useCallback(
    (field: keyof SignUpProfileFormData, value: string) => {
      const processedValue = field === 'name' ? value.toUpperCase() : value;
      setSignUpProfile((prev) => ({ ...prev, [field]: processedValue }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    []
  );

  useEffect(() => {
    const rawCpf = unmaskCpf(signUpProfile.cpf);
    if (rawCpf.length !== 11) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const exists = await StudentService.checkCpfExists(rawCpf, onboarding.studentId ?? undefined);
        if (!cancelled && exists) {
          setErrors((prev) => ({ ...prev, cpf: 'Este CPF já está cadastrado' }));
        }
      } catch { /* ignore network errors during typing */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [signUpProfile.cpf, onboarding.studentId]);

  useEffect(() => {
    const rawPhone = unmaskPhone(signUpProfile.phone);
    if (rawPhone.length < 10 || rawPhone.length > 11) return;
    const callingCode = extractCallingCode(signUpProfile.callingCode).replace(/\D/g, '') || '55';
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const exists = await PhoneService.checkPhoneExists(callingCode, rawPhone, onboarding.studentId ?? undefined);
        if (!cancelled && exists) {
          setErrors((prev) => ({ ...prev, phone: 'Este número de telefone já está cadastrado em outro perfil.' }));
        }
      } catch { /* ignore network errors during typing */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [signUpProfile.phone, signUpProfile.callingCode, onboarding.studentId]);

  const updateAnamnese = useCallback(
    <K extends keyof AnamneseFormData>(field: K, value: AnamneseFormData[K]) => {
      setAnamnese((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    []
  );

  const updateParQ = useCallback(
    <K extends keyof ParQFormData>(field: K, value: ParQFormData[K]) => {
      setParQ((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    []
  );

  const updatePhoto = useCallback(
    (position: keyof PhotosFormData, photo: PhotoFile | null) => {
      setPhotos((prev) => ({ ...prev, [position]: photo }));
    },
    []
  );

  const markStoredPhotoForRemoval = useCallback((position: keyof PhotosFormData) => {
    const photoPosition = PHOTO_FORM_KEY_TO_POSITION[position];
    setPendingPhotoDeletions((prev) => (
      prev.includes(photoPosition) ? prev : [...prev, photoPosition]
    ));
  }, []);

  const handleProfilePhotoButtonClick = useCallback(() => {
    if (isReadOnly || isUploadingProfilePhoto) {
      return;
    }

    clearProfilePhotoFeedback();
    profilePhotoInputRef.current?.click();
  }, [clearProfilePhotoFeedback, isReadOnly, isUploadingProfilePhoto]);

  const handleProfilePhotoInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    await selectProfilePhoto(file);
    event.target.value = '';
  }, [selectProfilePhoto]);

  const lastLookedUpCep = useRef('');

  const lookupCep = useCallback(async () => {
    const cep = signUpProfile.cep.replace(/\s/g, '');
    if (!cep || cep.length < 3) return;

    // For Brazil enforce 8 digits; for others just require a non-empty code
    const countryCode = signUpProfile.country || 'BR';
    if (countryCode === 'BR' && cep.replace(/\D/g, '').length !== 8) return;

    // Avoid duplicate lookups for the same postal code
    const key = `${countryCode}:${cep}`;
    if (lastLookedUpCep.current === key) return;
    lastLookedUpCep.current = key;

    setLoadingCep(true);
    try {
      const res = await fetch(`${config.supabaseFunctionsUrl}/address-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zipCode: cep,
          countryCode,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setErrors((prev) => ({ ...prev, cep: data?.error || 'Código postal não encontrado.' }));
        return;
      }
      setErrors((prev) => ({ ...prev, cep: '' }));
      setSignUpProfile((prev) => ({
        ...prev,
        street: data.street || prev.street,
        complement: data.complement || prev.complement,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state,
      }));
    } catch {
      lastLookedUpCep.current = ''; // allow retry on network error
      setErrors((prev) => ({ ...prev, cep: 'Erro ao buscar código postal.' }));
    } finally {
      setLoadingCep(false);
    }
  }, [signUpProfile.cep, signUpProfile.country]);

  const saveCoachAssignmentOnly = async (): Promise<ProfileSaveResult> => {
    const studentId = onboarding.studentId;
    if (!studentId) {
      throw new Error('Aluna não encontrada.');
    }

    const selectedCoachId = Number.parseInt(signUpProfile.coachId, 10);
    if (!Number.isInteger(selectedCoachId) || selectedCoachId <= 0) {
      setErrors({ coachId: 'Selecione a professora.' });
      throw new Error('Selecione a professora.');
    }

    await StudentService.upsertCoachAssociation(studentId, selectedCoachId);
    snapshotCoachId.current = signUpProfile.coachId;
    setErrors((prev) => {
      if (!prev.coachId) return prev;
      const { coachId: _coachId, ...rest } = prev;
      return rest;
    });

    return { studentId, shouldRedirect: false };
  };

  /* ── Step save functions ── */
  const saveProfile = async (): Promise<ProfileSaveResult> => {
    if (isCoachOnlyEditor) {
      return saveCoachAssignmentOnly();
    }

    const rawCpf = unmaskCpf(signUpProfile.cpf);
    const rawPhone = unmaskPhone(signUpProfile.phone);
    const rawCep = signUpProfile.cep.trim();
    const callingCode = extractCallingCode(signUpProfile.callingCode).replace(/\D/g, '') || '55';

    const profileValidation = validateSignUpProfile(signUpProfile, editMode, isForeignStudent);
    if (Object.keys(profileValidation).length > 0) {
      setErrors(profileValidation);
      throw new Error(profileValidation.cpf || 'Dados do cadastro incompletos.');
    }

    if (rawCpf) {
      const cpfExists = await StudentService.checkCpfExists(rawCpf, onboarding.studentId ?? undefined);
      if (cpfExists) {
        setErrors({ cpf: 'Este CPF já está cadastrado' });
        throw new Error('CPF duplicado');
      }
    }

    if (rawPhone) {
      const phoneExists = await PhoneService.checkPhoneExists(
        callingCode,
        rawPhone,
        onboarding.studentId ?? undefined
      );
      if (phoneExists) {
        setErrors({ phone: 'Este número de telefone já está cadastrado em outro perfil.' });
        throw new Error('Telefone duplicado');
      }
    }

    let studentId = onboarding.studentId;

    const rawHeight = unmaskHeight(signUpProfile.height);
    const rawWeight = unmaskWeight(signUpProfile.weight);
    const rawInstagram = unmaskInstagram(signUpProfile.instagram);

    const studentFields = isSupportPersonalInfoOnly
      ? {
          full_name: signUpProfile.name.trim(),
          cpf: rawCpf || null,
          birth_date: signUpProfile.birthDate || null,
          joined_at: signUpProfile.joinedAt || null,
          instagram_username: rawInstagram || null,
          is_foreign_student: isForeignStudent,
          is_trade_agreement: isTradeAgreement,
        }
      : {
          full_name: signUpProfile.name.trim(),
          email: signUpProfile.email.trim() || null,
          cpf: rawCpf || null,
          birth_date: signUpProfile.birthDate || null,
          joined_at: signUpProfile.joinedAt || null,
          instagram_username: rawInstagram || null,
          height_m: rawHeight ? parseFloat(rawHeight) : null,
          weight_kg: rawWeight ? parseFloat(rawWeight) : null,
          is_inactive: isInactive,
          is_foreign_student: isForeignStudent,
          is_trade_agreement: isTradeAgreement,
        };

    const selectedCoachId = Number.parseInt(signUpProfile.coachId, 10);
    const coachId = Number.isInteger(selectedCoachId) && selectedCoachId > 0
      ? selectedCoachId
      : null;
    const shouldCreateStudent = !studentId;

    const savedStudent = await StudentService.saveProfile({
      studentId: shouldCreateStudent ? null : studentId,
      student: shouldCreateStudent
        ? { user_id: null, ...studentFields }
        : studentFields,
      phone: {
        country_code: callingCode,
        phone_number: rawPhone || null,
        country_iso: CODE_TO_ISO[signUpProfile.callingCode] || 'BR',
      },
      address: {
        zipcode: rawCep || null,
        street: orNull(signUpProfile.street),
        street_number: orNull(signUpProfile.number),
        complement: orNull(signUpProfile.complement),
        neighborhood: orNull(signUpProfile.neighborhood),
        city: orNull(signUpProfile.city),
        state: orNull(signUpProfile.state),
        country: signUpProfile.country || 'BR',
      },
      coachId,
    });

    studentId = savedStudent.id;
    setClassification(savedStudent.classification ?? null);
    setProject(savedStudent.project ?? project);
    if (typeof savedStudent.is_reactivated === 'boolean') {
      setIsReactivated(savedStudent.is_reactivated);
    }

    if (!editMode) {
      setOnboarding({ userId: null, studentId });
    }

    if (coachId) {
      snapshotCoachId.current = signUpProfile.coachId;
    }

    // Persist auth user (username / password) if email or username is provided
    const trimmedEmail = signUpProfile.email.trim();
    const trimmedUsername = signUpProfile.username.trim();
    let shouldRedirect = true;

    if (!isSupportPersonalInfoOnly && (trimmedEmail || trimmedUsername)) {
      const authPayload: Record<string, string> = {};
      if (trimmedEmail) authPayload.email = trimmedEmail;
      if (trimmedUsername) authPayload.username = trimmedUsername;
      if (signUpProfile.password) authPayload.password = signUpProfile.password;

      // If student already has a linked auth user, update it; otherwise create
      const currentUserId = onboarding.userId ?? null;
      if (currentUserId) authPayload.userId = currentUserId;

      try {
        const authRes = await fetch(`${config.supabaseFunctionsUrl}/manage-auth-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(authPayload),
        });
        if (authRes.ok) {
          const authData = await authRes.json();
          if (authData.userId) {
            setOnboarding((prev) => ({ ...prev, userId: authData.userId }));
          }
        }
      } catch {
        // Auth user persistence is best-effort; do not block the rest of the save
      }
    }

    if (!isSupportPersonalInfoOnly && hasPendingProfilePhoto) {
      if (studentId) {
        await uploadPendingPhoto(studentId);
      } else {
        shouldRedirect = false;
      }
    }

    return { studentId: studentId!, shouldRedirect };
  };

  const saveAnamnese = async (): Promise<void> => {
    const studentId = onboarding.studentId!;
    const anamnesisId = selectedAnamnesisId ?? latestAnamnesisId;
    await AnamnesisService.upsertForStudent(
      studentId,
      mapStudentRegisterAnamnesisToRecord(anamnese),
      anamnesisId,
    );

    if (isViewingLatestAnamnesis) {
      const height_m = parseHeightMeters(anamnese.heightCm);
      const weight_kg = parseWeightKg(anamnese.weightKg);
      const rawInstagram = unmaskInstagram(anamnese.instagram);

      await StudentService.update(studentId, {
        height_m,
        weight_kg,
        instagram_username: rawInstagram || null,
      });

      const nextProfile = {
        ...signUpProfile,
        height: height_m != null ? formatHeightMeters(height_m) : '',
        weight: weight_kg != null ? formatWeightKg(weight_kg) : '',
        instagram: rawInstagram ? maskInstagram(rawInstagram) : '',
      };
      setSignUpProfile(nextProfile);
      snapshotProfile.current = JSON.stringify(nextProfile);
      anamnesisProfileDefaultsRef.current = {
        heightCm: anamnese.heightCm,
        weightKg: anamnese.weightKg,
        instagram: anamnese.instagram,
      };
    }

    if (anamnesisId) {
      const refreshedRows = await AnamnesisService.findAllByStudentId(studentId);
      setAnamnesisRecords(refreshedRows);
      const refreshedRecord = refreshedRows.find((row) => row.id === anamnesisId) ?? refreshedRows[0];
      if (refreshedRecord) {
        applyAnamnesisRecordToForms(refreshedRecord);
      }
    }
  };

  const saveParQ = async (): Promise<void> => {
    const studentId = onboarding.studentId!;
    const anamnesisId = selectedAnamnesisId ?? latestAnamnesisId;
    await AnamnesisService.upsertForStudent(
      studentId,
      mapStudentRegisterParqToAnamnesisRecord(parq),
      anamnesisId,
    );

    if (anamnesisId) {
      const refreshedRows = await AnamnesisService.findAllByStudentId(studentId);
      setAnamnesisRecords(refreshedRows);
      const refreshedRecord = refreshedRows.find((row) => row.id === anamnesisId) ?? refreshedRows[0];
      if (refreshedRecord) {
        applyAnamnesisRecordToForms(refreshedRecord);
      }
    }
  };

  const savePhotos = async (): Promise<void> => {
    const studentId = onboarding.studentId!;
    const photosToUpload = (Object.keys(photos) as Array<keyof PhotosFormData>)
      .filter((key) => Boolean(photos[key]?.file))
      .map((key) => ({
        position: PHOTO_FORM_KEY_TO_POSITION[key],
        file: photos[key]!.file!,
      }));
    const photosToDelete = [...pendingPhotoDeletions];

    if (photosToUpload.length === 0 && photosToDelete.length === 0) {
      return;
    }

    const uploadAssessmentDate = bodyPhotoAssessmentDatePartsToIso(bodyPhotoAssessmentDateParts);
    if (photosToUpload.length > 0 && !uploadAssessmentDate) {
      window.alert('Informe uma data válida para as fotos corporais (dia, mês e ano).');
      return;
    }

    if (photosToDelete.length > 0) {
      const deletionAssessmentDate = primaryBodyPhotoAssessmentDate
        && primaryBodyPhotoAssessmentDate !== 'legacy'
        ? primaryBodyPhotoAssessmentDate
        : undefined;

      await PhotoService.deleteBodyPhotos(studentId, photosToDelete, {
        assessmentDate: deletionAssessmentDate,
      });

      if (onboarding.userId && !deletionAssessmentDate) {
        await PhotoService.deleteLegacyUserBodyPhotos(onboarding.userId, photosToDelete);
      }
    }

    if (photosToUpload.length > 0 && uploadAssessmentDate) {
      const uploadPositions = photosToUpload.map(({ position }) => position);
      await PhotoService.deleteBodyPhotos(studentId, uploadPositions, {
        assessmentDate: uploadAssessmentDate,
      });

      await PhotoService.uploadBodyPhotos(studentId, photosToUpload, {
        assessmentDate: uploadAssessmentDate,
      });
    }

    const refreshedAssessments = await PhotoService.listBodyPhotoAssessments(
      studentId,
      onboarding.userId,
      { bustCache: true },
    );
    setPhotoAssessments(refreshedAssessments);
    setPrimaryBodyPhotoAssessmentDate(refreshedAssessments[0]?.assessmentDate ?? null);
    setPendingPhotoDeletions([]);
    setBodyPhotoAssessmentDateParts(getTodayBodyPhotoAssessmentDateParts());

    (Object.keys(photos) as Array<keyof PhotosFormData>).forEach((key) => {
      const photo = photos[key];
      if (photo?.preview.startsWith('blob:')) {
        URL.revokeObjectURL(photo.preview);
      }
    });
    const emptyPhotos = createEmptyPhotosFormData();
    setPhotos(emptyPhotos);
    snapshotPhotos.current = serializePhotosSnapshot(emptyPhotos, []);

    photosLoadedStudentIdRef.current = studentId;
  };

  /* ── Step configuration (validation + save) ── */
  const stepConfigs: Record<string, {
    validate: (() => Record<string, string>) | null;
    save: (() => Promise<void>) | null;
  }> = {
    'personal-info': { validate: () => validateSignUpProfile(signUpProfile, editMode, isForeignStudent), save: null },
    anamnesis: { validate: () => validateAnamnese(anamnese), save: saveAnamnese },
    parq: { validate: () => validateParQ(parq), save: saveParQ },
    photos: { validate: null, save: savePhotos },
    payments: { validate: null, save: null },
    history: { validate: null, save: null },
    trainings: { validate: null, save: null },
  };

  const validateCurrentStep = (): boolean => {
    const config = stepConfigs[activeKey];
    if (!config?.validate) return true;
    const stepErrors = config.validate();
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  /* ── Save handler ── */
  const handleDeleteStudent = async () => {
    const studentId = onboarding.studentId;
    if (!studentId || !canManageStudent) {
      return;
    }

    try {
      setDeleting(true);
      await StudentService.deleteWithRelations(studentId);
      setShowDeleteModal(false);
      navigate('/alunas', { replace: true });
    } catch {
      alert('Erro ao excluir aluna. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  const executeStatusChange = async () => {
    const studentId = onboarding.studentId;
    if (!studentId) {
      setError('Aluna não encontrada.');
      return;
    }

    const activating = !isInactive && snapshotIsInactive.current;
    const inactivating = isInactive && !snapshotIsInactive.current;

    if (!activating && !inactivating) {
      return;
    }

    setSaving(true);
    setError(null);
    setErrors({});

    try {
      if (inactivating) {
        await StudentService.updateStatus(studentId, true, inactivationReason, inactivationObservation);
      } else {
        await StudentService.updateStatus(studentId, false);
      }

      snapshotIsInactive.current = isInactive;
      setSavedIsInactive(isInactive);
      setInactivationReason('');
      setInactivationObservation(null);
      if (activating) {
        setLatestInactivationReason(null);
        setLatestInactivationObservation(null);
      } else if (inactivating) {
        setLatestInactivationReason(inactivationReason);
        setLatestInactivationObservation(inactivationObservation);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar status da aluna.';
      setError(message);
      if (inactivating) {
        setIsInactive(false);
        setInactivationReason('');
        setInactivationObservation(null);
        if (!snapshotIsInactive.current) {
          setLatestInactivationReason(null);
          setLatestInactivationObservation(null);
        }
      } else if (activating) {
        setIsInactive(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const executeSave = async () => {
    if (!isCoachOnlyEditor && !validateCurrentStep()) return;

    setSaving(true);
    setError(null);
    try {
      let redirectId: number | null = onboarding.studentId;
      let shouldRedirect = true;

      if (activeKey === 'personal-info') {
        const profileSaveResult = await saveProfile();
        redirectId = profileSaveResult.studentId;
        shouldRedirect = profileSaveResult.shouldRedirect;
      } else {
        const saver = stepConfigs[activeKey]?.save;
        if (saver) await saver();
      }

      if (redirectId && shouldRedirect && activeKey !== 'photos') {
        window.location.href = `/alunas/${redirectId}/editar`;
        return;
      }

      /* Update snapshots so isDirty resets */
      snapshotProfile.current = JSON.stringify(signUpProfile);
      snapshotAnamnese.current = JSON.stringify(anamnese);
      snapshotParq.current = JSON.stringify(parq);
      snapshotIsInactive.current = isInactive;
      snapshotIsForeignStudent.current = isForeignStudent;
      snapshotIsTradeAgreement.current = isTradeAgreement;
      snapshotCoachId.current = signUpProfile.coachId;
      setSavedIsInactive(isInactive);
      setInactivationReason('');
      setInactivationObservation(null);

    } catch (err: any) {
      if (!err.message?.includes('duplicado')) {
        setError(err.message || 'Erro ao salvar dados da aluna.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const canSaveThisSection = activeKey === 'personal-info'
      ? (canSavePersonalInfo || canChangeCoach)
      : canSaveStudent;

    if (!canSaveThisSection && !isReactivating) {
      setError(editMode
        ? (isSupportPersonalInfoOnly
          ? 'O perfil de suporte só pode editar as informações pessoais da aluna.'
          : isCoachOnlyEditor
            ? 'Apenas a professora da aluna pode ser alterada neste perfil.'
            : 'Apenas administradoras e comercial podem editar alunas.')
        : 'Apenas administradoras podem criar novas alunas.');
      return;
    }

    if (isInactive && !snapshotIsInactive.current) {
      if (!inactivationReason) {
        setShowInactivateReasonModal(true);
        return;
      }
      setShowInactivateModal(true);
      return;
    }

    if (!isInactive && snapshotIsInactive.current) {
      await executeStatusChange();
      return;
    }

    await executeSave();
  };

  const handleInactivateReasonProceed = (reason: string, observation: string | null) => {
    setInactivationReason(reason);
    setInactivationObservation(observation);
    setLatestInactivationReason(reason);
    setLatestInactivationObservation(observation);
    setShowInactivateReasonModal(false);
    setShowInactivateModal(true);
  };

  const handleCancelInactivateFlow = () => {
    setShowInactivateReasonModal(false);
    setShowInactivateModal(false);
    setInactivationReason('');
    setInactivationObservation(null);
    if (!snapshotIsInactive.current) {
      setLatestInactivationReason(null);
      setLatestInactivationObservation(null);
    }
    setIsInactive(false);
  };

  if (loadingData || authLoading) {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  /* ── Profile two-column layout (tabs 0 and 1) ── */

  const renderCopyLinkButton = (linkTarget: StudentPublicLinkTarget, label?: string) => (
    onboarding.studentId
      ? (
        <StudentPublicLinkCopyButton
          studentId={onboarding.studentId}
          linkTarget={linkTarget}
          label={label}
        />
      )
      : null
  );

  const renderProfileContent = () => (
    <ProfileColumns>
      <div className={styles.columnLeft}>
        <h2 className={styles.columnTitle}>Dados de acesso e segurança</h2>
        <p className={styles.photoLabel}>Foto de perfil</p>
        <div className={styles.photoSection}>
          <div className={styles.avatarRow}>
            <AvatarProfile size={101} src={profilePhotoUrl} />
            <div className={styles.photoActions}>
              <PhotoUploadButton
                disabled={isReadOnly || isUploadingProfilePhoto}
                onClick={handleProfilePhotoButtonClick}
              >
                {isUploadingProfilePhoto ? 'Enviando foto...' : 'Inserir foto'}
              </PhotoUploadButton>
              <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleProfilePhotoInputChange}
                className={styles.hiddenFileInput}
              />
            </div>
          </div>
          {profilePhotoFeedback && (
            <AlertBanner
              message={profilePhotoFeedback.message}
              variant={profilePhotoFeedback.variant}
              className={styles.photoFeedback}
            />
          )}
        </div>
        {(project || shouldDisplayStudentClassificationBadge(classification) || isReactivated) && (
          <div className={styles.studentStatusRow}>
            <ProjectBadge project={project} />
            {shouldDisplayStudentClassificationBadge(classification) && (
              <Badge
                variant={getStudentClassificationBadgeVariant(classification)}
                className={styles.newStudentBadge}
              >
                {classification.label}
              </Badge>
            )}
            <ReactivatedBadge visible={isReactivated} />
          </div>
        )}

        <div className={styles.leftFields}>
          <FormInput
            label="Nome de usuário"
            name="username"
            value={signUpProfile.username}
            onChange={(e) => updateSignUpProfile('username', e.target.value)}
            placeholder="Inserir nome de usuário"
            error={errors.username}
            disabled={isReadOnly}
          />
          <FormInput
            label="E-mail"
            type="email"
            name="email"
            value={signUpProfile.email}
            onChange={(e) => updateSignUpProfile('email', e.target.value)}
            placeholder="Seu melhor e-mail"
            error={errors.email}
            disabled={isReadOnly}
          />
          <FormSelect
            label="Status"
            id="status"
            value={String(isInactive)}
            onChange={(e) => {
              const nextInactive = e.target.value === 'true';
              setIsInactive(nextInactive);
              if (!nextInactive && !snapshotIsInactive.current) {
                setLatestInactivationReason(null);
                setLatestInactivationObservation(null);
                setInactivationReason('');
                setInactivationObservation(null);
              }
            }}
            options={[
              { value: 'false', label: 'Ativa' },
              { value: 'true', label: 'Inativa' },
            ]}
            placeholder="Selecione o status"
            disabled={!canChangeStudentStatus}
          />
          {isInactive && (latestInactivationReason || inactivationReason) && (
            <p className={styles.inactivationReasonHint}>
              Motivo: {getStudentInactivationReasonLabel(latestInactivationReason || inactivationReason)}
              {(latestInactivationObservation || inactivationObservation)?.trim()
                ? ` — ${(latestInactivationObservation || inactivationObservation)?.trim()}`
                : ''}
            </p>
          )}
        </div>
        {editMode && onboarding.studentId ? (
          <PerformanceSupportSection
            studentId={onboarding.studentId}
            readOnly={isPersonalInfoReadOnly}
          />
        ) : null}
      </div>

      {/* ── Right column: Informações básicas ── */}
      <div className={styles.columnRight}>
        <SignUpProfileStep
          data={signUpProfile}
          errors={errors}
          onFieldChange={updateSignUpProfile}
          onCepBlur={lookupCep}
          isForeignStudent={isForeignStudent}
          isTradeAgreement={isTradeAgreement}
          onForeignStudentChange={(value) => {
            setIsForeignStudent(value);
            if (value) {
              setErrors((prev) => ({ ...prev, cpf: '' }));
            }
          }}
          onTradeAgreementChange={setIsTradeAgreement}
          editMode={editMode}
          hideSidebarFields
          loadingCep={loadingCep}
          readOnly={isPersonalInfoReadOnly}
          coachReadOnly={isCoachFieldReadOnly}
          coachOptions={coachOptions}
          loadingCoachOptions={loadingCoachOptions}
          titleAction={renderCopyLinkButton('onboarding')}
        />
      </div>
    </ProfileColumns>
  );

  return (
    <div className={styles.container}>
      {savedIsInactive && <AlertBanner message="Aluna inativa. Não é possível fazer alterações." variant="warning" />}
      {error && <AlertBanner message={error} variant="error" />}

      <div className={styles.profileShell}>
        <StickyHeader
          className={styles.stickyHeader}
          contentClassName={styles.stickyHeaderContent}
          topBarClassName={styles.topBar}
          actionsClassName={styles.topBarRight}
          leftContent={
            editMode && canManageStudent && onboarding.studentId ? (
              <Button
                variant="ghost"
                size="small"
                type="button"
                icon="delete"
                onClick={() => setShowDeleteModal(true)}
                aria-label="Excluir aluna"
              >
                Excluir aluna
              </Button>
            ) : undefined
          }
          onCancel={() => navigate('/alunas')}
          onSave={handleSave}
          isSaving={saving}
          disabled={
            saving
            || isUploadingProfilePhoto
            || !isDirty
            || ((activeKey === 'personal-info' ? isPersonalInfoSectionLocked : isReadOnly) && !isReactivating)
            || activeKey === 'history'
            || activeKey === 'trainings'
            || activeKey === 'behavioral-profile'
          }
          saveVariant="secondary"
          saveButtonClassName={styles.btnSave}
        />

        <AdminTabbedPanel
          tabs={steps}
          activeKey={activeKey}
          onTabChange={setActiveKey}
          className={styles.profilePanel}
          tabBarClassName={styles.tabBarReset}
          contentClassName={styles.contentBg}
          padded={false}
        >
          {activeKey === 'personal-info' && renderProfileContent()}
          {activeKey === 'anamnesis' && (
            <>
              {renderAnamnesisRecordSelector('Registro da anamnese')}
              <AnamneseStep
                data={anamnese}
                errors={errors}
                onFieldChange={updateAnamnese}
                readOnly={isReadOnly}
                titleAction={renderCopyLinkButton('anamnese')}
                studentId={onboarding.studentId}
                canGenerateAiSummary={Boolean(onboarding.studentId) && !isReadOnly}
                aiSummary={anamneseAiSummary}
                onAiSummaryGenerated={setAnamneseAiSummary}
              />
            </>
          )}
          {activeKey === 'parq' && (
            <>
              {renderAnamnesisRecordSelector('Registro do PAR-Q')}
              <ParQStep
                data={parq}
                errors={errors}
                onFieldChange={updateParQ}
                readOnly={isReadOnly}
                titleAction={renderCopyLinkButton('parq')}
              />
            </>
          )}
          {activeKey === 'photos' && (
            <PhotoGuidelinesStep
              data={photos}
              assessmentDateParts={bodyPhotoAssessmentDateParts}
              onAssessmentDatePartsChange={setBodyPhotoAssessmentDateParts}
              onPhotoChange={updatePhoto}
              onMarkStoredPhotoForRemoval={markStoredPhotoForRemoval}
              disabled={isReadOnly}
              isLoading={loadingPhotos}
              titleAction={renderCopyLinkButton('enviar-fotos')}
              showHistory={editMode}
              assessments={photoAssessments}
              hasPendingBodyPhotoChanges={hasPendingBodyPhotoChanges}
              onSaveBodyPhotos={onboarding.studentId ? savePhotos : undefined}
            />
          )}
          {activeKey === 'equipments' && onboarding.studentId && (
            <StudentEquipmentsStep
              studentId={onboarding.studentId}
              titleAction={renderCopyLinkButton('equipamentos')}
              refreshKey={equipmentRefreshKey}
            />
          )}
          {activeKey === 'equipments' && !onboarding.studentId && (
            <div className={styles.emptyState}>
              <Icon name="fitness_center" size={48} />
              <span>Salve as informações pessoais primeiro para gerenciar equipamentos.</span>
            </div>
          )}
          {activeKey === 'payments' && canViewPayments && onboarding.studentId && (
            <PaymentDetails
              studentId={onboarding.studentId}
              studentName={signUpProfile.name}
              studentCpf={signUpProfile.cpf}
              studentEmail={signUpProfile.email}
              canManageConsultancyCredit={canSavePersonalInfo && !savedIsInactive}
            />
          )}
          {activeKey === 'payments' && canViewPayments && !onboarding.studentId && (
            <div className={styles.emptyState}>
              <Icon name="payments" size={48} />
              <span>Salve as informações pessoais primeiro para gerenciar pagamentos.</span>
            </div>
          )}
          {activeKey === 'behavioral-profile' && canViewBehavioralProfile && onboarding.studentId && (
            <BehavioralProfileStep studentId={onboarding.studentId} />
          )}
          {activeKey === 'history' && onboarding.studentId && (
            <StudentHistoryStep studentId={onboarding.studentId} />
          )}
          {activeKey === 'trainings' && onboarding.studentId && (
            <StudentTrainingsStep studentId={onboarding.studentId} />
          )}
        </AdminTabbedPanel>
      </div>

      <DeleteStudentConfirmModal
        open={canManageStudent && showDeleteModal && Boolean(onboarding.studentId)}
        studentName={signUpProfile.name}
        loading={deleting}
        onConfirm={handleDeleteStudent}
        onCancel={() => setShowDeleteModal(false)}
      />

      <StudentInactivationReasonModal
        open={showInactivateReasonModal}
        studentName={signUpProfile.name || null}
        onProceed={handleInactivateReasonProceed}
        onCancel={handleCancelInactivateFlow}
      />

      <ConfirmModal
        open={showInactivateModal}
        title="Inativar aluna"
        message={`Tem certeza que deseja inativar ${signUpProfile.name || 'esta aluna'}? Enquanto inativa, o cadastro não poderá ser editado.`}
        confirmLabel="Inativar"
        cancelLabel="Voltar"
        variant="warning"
        icon="person_off"
        loading={saving}
        onConfirm={() => {
          setShowInactivateModal(false);
          void executeStatusChange();
        }}
        onCancel={handleCancelInactivateFlow}
      />
    </div>
  );
};

export default StudentRegister;
